import React, { useState } from 'react';
import { Card, DatePicker, Space, Table, Tabs } from 'antd';
import dayjs from 'dayjs';
import treOverheadReportsService from '@/services/treOverheadReports.service';

const { RangePicker } = DatePicker;

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const HIDDEN_KEYS = new Set(['id', 'company_id', 'created_by', 'updated_by', 'is_deleted']);

function isIdKey(key: string): boolean {
  const k = key.toLowerCase();
  return HIDDEN_KEYS.has(k) || k.endsWith('_id') || k === 'uuid';
}

function formatCell(v: unknown): string {
  if (v == null || v === '') return '—';
  if (typeof v === 'number') return v.toLocaleString();
  const s = String(v).trim();
  if (UUID_RE.test(s)) return '—';
  return s;
}

function titleize(key: string): string {
  return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

const TradingRentalOverheadReportsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState('overhead-analysis');
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);
  const [data, setData] = useState<Record<string, unknown>[]>([]);
  const [loading, setLoading] = useState(false);

  const filters = {
    fromDate: dateRange?.[0]?.format('YYYY-MM-DD'),
    toDate: dateRange?.[1]?.format('YYYY-MM-DD'),
  };

  const loadReport = async (tab: string) => {
    setLoading(true);
    try {
      let rows: unknown[] = [];
      switch (tab) {
        case 'overhead-analysis':
          rows = await treOverheadReportsService.overheadAnalysis(filters);
          break;
        case 'trading-profitability':
          rows = await treOverheadReportsService.tradingProfitability(filters);
          break;
        case 'rental-profitability':
          rows = await treOverheadReportsService.rentalProfitability(filters);
          break;
        case 'quotation-margin':
          rows = await treOverheadReportsService.quotationMarginAnalysis(filters);
          break;
        case 'project-commercial':
          rows = await treOverheadReportsService.projectCommercialAnalysis(filters);
          break;
        case 'commercial-charges':
          rows = await treOverheadReportsService.commercialChargesAnalysis(filters);
          break;
        case 'gross-profit':
          rows = await treOverheadReportsService.grossProfitReport(filters);
          break;
        default:
          rows = [];
      }
      setData(Array.isArray(rows) ? (rows as Record<string, unknown>[]) : []);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    void loadReport(activeTab);
  }, [activeTab, dateRange?.[0]?.format('YYYY-MM-DD'), dateRange?.[1]?.format('YYYY-MM-DD')]);

  const columns =
    data.length > 0
      ? Object.keys(data[0])
          .filter((key) => !isIdKey(key))
          .map((key) => ({
            title: titleize(key),
            dataIndex: key,
            key,
            render: (v: unknown) => formatCell(v),
          }))
      : [];

  const tabs = [
    { key: 'overhead-analysis', label: 'Overhead Analysis' },
    { key: 'trading-profitability', label: 'Trading Profitability' },
    { key: 'rental-profitability', label: 'Rental Profitability' },
    { key: 'quotation-margin', label: 'Quotation Margin' },
    { key: 'project-commercial', label: 'Project Commercial' },
    { key: 'commercial-charges', label: 'Commercial Charges' },
    { key: 'gross-profit', label: 'Gross Profit' },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card title="Trading & Rental Overhead Reports">
        <Space style={{ marginBottom: 16 }}>
          <RangePicker value={dateRange} onChange={(v) => setDateRange(v as [dayjs.Dayjs, dayjs.Dayjs] | null)} />
        </Space>
        <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabs} />
        <Table
          rowKey={(_, idx) => String(idx)}
          loading={loading}
          dataSource={data}
          columns={columns}
          scroll={{ x: true }}
          pagination={{ pageSize: 25 }}
        />
      </Card>
    </div>
  );
};

export default TradingRentalOverheadReportsPage;
